import { Navigate, useLocation, useParams } from "react-router-dom";
import { useTenantPathFn } from "@/lib/tenant";

import {
  LEGACY_TENANT_TABS,
  TENANT_CONNECTION_ANCHORS,
  TENANT_CONNECTION_PATH,
  type TenantConnectionAnchor,
} from "./tenantConnection";

/**
 * The addresses the Tenant connection page replaced, as redirects.
 *
 * /admin/tenant/:tab was the first single screen with Credentials /
 * Capabilities / Permissions / Consent tabs; settings/connection,
 * settings/tenant-init, settings/capabilities, settings/permissions and
 * settings/consent were the five pages that followed it. Every one now lands
 * on the matching block of /admin/settings/tenant-connection, which opens
 * because its anchor is the hash.
 *
 * They are kept rather than deleted: links to them exist in the docs, in
 * bookmarks and — for settings/consent — in the server itself, whose
 * admin-consent return lands there with ?admin_consent=… or ?error=…. So the
 * query string travels with the redirect, and the redirect goes STRAIGHT to the
 * final page: the old chain through /admin/settings lost the hash on its
 * second hop.
 */
function useConnectionTarget(anchor: TenantConnectionAnchor) {
  const resolve = useTenantPathFn();
  const location = useLocation();
  return { pathname: resolve(TENANT_CONNECTION_PATH), search: location.search, hash: `#${anchor}` };
}

/** settings/{connection,tenant-init,capabilities,permissions,consent} → one block. */
export function LegacySettingsRedirect({ anchor }: { anchor: TenantConnectionAnchor }) {
  return <Navigate to={useConnectionTarget(anchor)} replace />;
}

/** /admin/tenant and /admin/tenant/:tab. */
export function AdminTenantPage() {
  const { tab } = useParams<{ tab?: string }>();
  const anchor =
    (tab === undefined ? undefined : LEGACY_TENANT_TABS[tab]) ??
    TENANT_CONNECTION_ANCHORS.entraApplication;
  return <Navigate to={useConnectionTarget(anchor)} replace />;
}

export default AdminTenantPage;
